import React, { useState } from 'react';
import { X, ChevronLeft, ExternalLink, MapPin, Star, DollarSign } from 'lucide-react';

interface FacilityDailyFees {
    basic: number;
    dap: number;
    nccc: number;
    hsc: number;
    extraServices: number;
}

export interface Facility {
    id: number;
    name: string;
    suburb: string;
    state: string;
    radPrice: number;
    dailyFees?: FacilityDailyFees;
    distance: number;
    rating: number;
    careLevels: string[];
    amenities: string[];
    websiteUrl?: string;
    images?: string[];
}

type FacilitySortBy = 'distance' | 'price' | 'rating';

interface FacilitySearchModalProps {
    facilities: Facility[];
    onSelect: (facility: Facility) => void;
    onClose: () => void;
}

const FacilitySearchModal = ({ facilities, onSelect, onClose }: FacilitySearchModalProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [maxDistance, setMaxDistance] = useState(10);
    const [sortBy, setSortBy] = useState<FacilitySortBy>('distance');
    const [showResults, setShowResults] = useState(false);

    const filteredFacilities = facilities
        .filter(facility => facility.distance <= maxDistance)
        .sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return a.radPrice - b.radPrice;
                case 'rating':
                    return b.rating - a.rating;
                default:
                    return a.distance - b.distance;
            }
        });

    const handleSearch = () => {
        if (searchTerm.trim()) {
            setShowResults(true);
        }
    };

    const handleBackToSearch = () => {
        setShowResults(false);
        setSearchTerm('');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            {showResults ? `Homes near ${searchTerm}` : 'Find a home'}
                        </h2>
                        {/* The list is sample data until a real directory is wired (defect 10, 17 Oct 2026) — say so
                            rather than let a reader take an invented home for a real one. */}
                        <p className="mt-1 text-[13px] text-slate-500" data-testid="facility-search-sample-note">
                            Example homes only, to show how the search will work. Use the room price from the home's own quote or My Aged Care.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-all duration-300 hover:scale-110"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    {!showResults ? (
                        <div className="space-y-6">
                            <div className="text-center">
                                <p className="text-gray-600 mb-6">Enter your suburb or area to find nearby aged care facilities</p>
                            </div>
                            <div className="max-w-md mx-auto space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Suburb or Area</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="e.g., Croydon, Five Dock, Campsie..."
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Distance: {maxDistance}km</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="20"
                                        value={maxDistance}
                                        onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                </div>
                                <button
                                    onClick={handleSearch}
                                    disabled={!searchTerm.trim()}
                                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed text-lg font-semibold hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Search homes
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <button
                                    onClick={handleBackToSearch}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <ChevronLeft size={20} />
                                    Back to Search
                                </button>
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700">Sort By:</label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value as FacilitySortBy)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="distance">Distance</option>
                                        <option value="price">RAD Price</option>
                                        <option value="rating">Rating</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {filteredFacilities.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No facilities found matching your criteria.
                                    </div>
                                ) : (
                                    filteredFacilities.map(facility => (
                                        <div
                                            key={facility.id}
                                            className="border border-gray-200 rounded-xl p-4 md:p-5 hover:border-blue-300 hover:shadow-md transition-all"
                                        >
                                            <div className="flex flex-col md:flex-row gap-5">
                                                <div className="shrink-0 w-full md:w-[min(100%,280px)] md:max-w-[280px]">
                                                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200/80">
                                                        {facility.images?.[0] ? (
                                                            <img
                                                                src={facility.images[0]}
                                                                alt={facility.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">No photo</div>
                                                        )}
                                                    </div>
                                                    {facility.images && facility.images.length > 1 ? (
                                                        <div className="flex gap-1.5 mt-2">
                                                            {facility.images.slice(1, 4).map((image, index) => (
                                                                <img
                                                                    key={index}
                                                                    src={image}
                                                                    alt={`${facility.name} view ${index + 2}`}
                                                                    className="h-14 min-w-0 flex-1 object-cover rounded-lg border border-slate-200"
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{facility.name}</h3>
                                                        <p className="text-gray-600 mt-0.5">{facility.suburb}, {facility.state}</p>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-600">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <MapPin size={15} className="text-sky-600 shrink-0" aria-hidden />
                                                                {facility.distance}km away
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <Star size={15} className="text-amber-500 shrink-0" aria-hidden />
                                                                {facility.rating}/5
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                                                                <DollarSign size={15} className="text-emerald-600 shrink-0" aria-hidden />
                                                                ${facility.radPrice.toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="mt-3">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Care levels</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{facility.careLevels.join(', ')}</p>
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amenities</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{facility.amenities.join(', ')}</p>
                                                        </div>
                                                        {facility.websiteUrl ? (
                                                            <a
                                                                href={facility.websiteUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <ExternalLink size={14} aria-hidden />
                                                                Visit website
                                                                <span className="text-xs font-normal text-gray-500">(demo URL)</span>
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                    <div className="shrink-0 flex lg:flex-col justify-end lg:justify-center gap-2 lg:w-36">
                                                        <button
                                                            type="button"
                                                            onClick={() => onSelect(facility)}
                                                            className="w-full lg:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 font-semibold shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                        >
                                                            Select
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FacilitySearchModal;
